import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Truck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoMode } from '../../hooks/useDemoMode';
import { DEMO_NAV, classificationBadge } from '../../config/navigation';
import { Button } from '../ui/Button';
import { BrandLogo } from '../branding/BrandLogo';
import { useShellNav } from '../../contexts/ShellNavContext';

export const DemoSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { setDemoMode } = useDemoMode();
  const { isSidebarOpen: isOpen, closeSidebar } = useShellNav();

  // Close drawer on navigation (mobile)
  React.useEffect(() => {
    closeSidebar();
  }, [location.pathname, location.search, closeSidebar]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSidebar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeSidebar]);

  const exitDemo = () => {
    setDemoMode(false);
    navigate('/dashboard', { replace: true });
  };

  const linkClass = (isActive: boolean) =>
    `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
      isActive ? 'bg-amber-500/25 text-white font-semibold' : 'text-white/85 hover:bg-white/10'
    }`;

  return (
    <>
      {/* Backdrop only — hamburger lives in AppNavbar */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={closeSidebar}
          aria-hidden
        />
      )}

      <aside
        id="demo-sidebar"
        className={`fixed left-0 top-0 z-40 h-screen w-64 border-r border-amber-500/30 bg-[#2D1537] transition-transform md:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-amber-500/30 px-3 pb-3 pt-4">
            <div className="flex justify-center">
              <BrandLogo variant="full" surface="dark" size="lg" imgClassName="mx-auto" />
            </div>
            <p className="mt-2 text-center text-[10px] font-semibold uppercase tracking-wider text-amber-200/90">
              Demo Workspace
            </p>
          </div>

          <nav className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
            {DEMO_NAV.map((section) => (
              <div key={section.name}>
                <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-white/50">
                  {section.name}
                </p>
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/demo'}
                      onClick={closeSidebar}
                      className={({ isActive }) => linkClass(isActive)}
                    >
                      <Truck size={16} className="opacity-70" />
                      <span className="flex-1">{item.name}</span>
                      {classificationBadge(item.classification) && (
                        <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] text-amber-200">
                          {classificationBadge(item.classification)}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="space-y-2 border-t border-white/10 p-3">
            <Button type="button" variant="primary" size="md" className="w-full" onClick={exitDemo}>
              Exit demo workspace
            </Button>
            <Button
              type="button"
              variant="outline"
              size="md"
              className="w-full border-white/20 text-white"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
            >
              <LogOut size={16} className="mr-2" />
              Sign out {user?.firstName ? `(${user.firstName})` : ''}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default DemoSidebar;
